import type { CollectorContributionContext } from '@/lib/community-collector.storage';
import type { PhraseEntry, SpeakContributeData } from './types';

/**
 * Non-visual rules of the "Traduire" session, kept pure so tests lock the
 * payloads sent to the API, the batch transitions and the round trip with
 * "Enregistrer". Behaviour is the page's historical one, moved here unchanged.
 */

/** Phrases requested per page from getTranslationSourceDataset. */
export const TRANSCRIPTION_PHRASE_PAGE_SIZE = 5;

/** Moderators and admins apply a translation at once; others send a proposal. */
export const appliesTranslationDirectly = (role: string | undefined) => role === 'moderator' || role === 'admin';

/** The text sent as the phrase to translate. */
export const phraseTextOf = (phrase: PhraseEntry) => phrase.text || phrase.frenchText || '';

export type TranslationSubmission =
  | { kind: 'direct'; args: [lang: string, phrase: string, translation: string, target: 'local'] }
  | {
    kind: 'proposal';
    args: [
      lang: string,
      fr: string,
      local: string,
      opts: { source: string; domain: string; communityProfileId?: string; communityCode?: string },
    ];
  };

/**
 * The call a submit makes: wordApi.updateTranslation for a moderator, else
 * transcriptionFrenchApi.submitPhraseTranslation with the collector context.
 */
export function translationSubmission({
  role,
  language,
  phrase,
  translatedText,
  collector,
}: {
  role: string | undefined;
  language: string;
  phrase: PhraseEntry;
  translatedText: string;
  collector: CollectorContributionContext | null;
}): TranslationSubmission {
  const phraseText = phraseTextOf(phrase);
  const translation = translatedText.trim();
  if (appliesTranslationDirectly(role)) {
    return { kind: 'direct', args: [language, phraseText, translation, 'local'] };
  }
  return {
    kind: 'proposal',
    args: [
      language,
      phraseText,
      translation,
      {
        source: phrase.source || '',
        domain: phrase.domain || '',
        communityProfileId: collector?.communityProfileId,
        communityCode: collector?.communityProfileCode,
      },
    ],
  };
}

/** What the collector dashboard remembers of a submitted translation. */
export const collectorContribution = (phrase: PhraseEntry, translatedText: string) => ({
  type: 'transcription' as const,
  content: `${phraseTextOf(phrase)} => ${translatedText.trim()}`,
});

/**
 * Why a submit stops before reaching the API, in the historical order:
 * empty text, a submit already running, then the validation gate (a
 * community-profile submission goes through the gate).
 */
export function translationSubmitBlocker({
  hasCurrent,
  translatedText,
  isSubmitting,
  gateBlocked,
  communityProfileId,
}: {
  hasCurrent: boolean;
  translatedText: string;
  isSubmitting: boolean;
  gateBlocked: boolean;
  communityProfileId?: string;
}): 'textRequired' | 'busy' | 'gate' | null {
  if (!hasCurrent || !translatedText.trim()) return 'textRequired';
  if (isSubmitting) return 'busy';
  if (!communityProfileId && gateBlocked) return 'gate';
  return null;
}

/** Index of the first phrase neither done nor skipped, else 0. */
export function firstOpenIndex(phrases: PhraseEntry[], completed: Set<string>, skipped: Set<string>): number {
  const index = phrases.findIndex((p) => !completed.has(p.id) && !skipped.has(p.id));
  return index !== -1 ? index : 0;
}

export type PhraseStep =
  | { type: 'goto'; index: number }
  | { type: 'nextPage' }
  | { type: 'complete' };

/** After a submit: the next open phrase, the next page, the end, or the last phrase. */
export function stepAfterSubmit({
  phrases,
  currentIndex,
  completed,
  skipped,
  hasMore,
}: {
  phrases: PhraseEntry[];
  currentIndex: number;
  completed: Set<string>;
  skipped: Set<string>;
  hasMore: boolean;
}): PhraseStep {
  const next = phrases.findIndex((p, idx) => idx > currentIndex && !completed.has(p.id) && !skipped.has(p.id));
  if (next !== -1) return { type: 'goto', index: next };
  const allDone = phrases.every((p) => completed.has(p.id) || skipped.has(p.id));
  if (allDone && hasMore) return { type: 'nextPage' };
  if (allDone) return { type: 'complete' };
  return { type: 'goto', index: phrases.length - 1 };
}

/**
 * After a skip: the next open phrase after this one, the next page, or
 * nothing left (`skipped` is the set before this skip).
 */
export function stepAfterSkip({
  phrases,
  currentIndex,
  currentId,
  completed,
  skipped,
  hasMore,
}: {
  phrases: PhraseEntry[];
  currentIndex: number;
  currentId: string | undefined;
  completed: Set<string>;
  skipped: Set<string>;
  hasMore: boolean;
}): { type: 'goto'; index: number } | { type: 'nextPage' } | { type: 'none' } {
  const next = phrases.findIndex((p, idx) => idx > currentIndex && !completed.has(p.id) && !skipped.has(p.id) && p.id !== currentId);
  if (next !== -1) return { type: 'goto', index: next };
  return hasMore ? { type: 'nextPage' } : { type: 'none' };
}

/**
 * The single phrase opened from "Enregistrer": the local phrase when it must
 * be translated to French, else the French phrase. Null when nothing came.
 */
export function speakContributePhrase(state: SpeakContributeData | null | undefined, now: number): PhraseEntry | null {
  if (!state?.fromSpeakContribute) return null;
  const phraseText = state.needsTranslationToFrench ? state.localPhraseToTranslate : state.frenchPhrase;
  if (!phraseText) return null;
  return { id: `speak-contribute-${now}`, text: phraseText, frenchText: phraseText, localText: '', source: '', domain: '' };
}

/** Where "back" goes when the page was opened from "Enregistrer", with the state it hands back. */
export function speakContributeReturn(state: SpeakContributeData | null | undefined, currentLang: string) {
  if (!state?.fromSpeakContribute || !state.returnTo) return null;
  return {
    to: state.returnTo || `/${currentLang}/speak-contribute`,
    state: { page: state.currentPage, currentSentence: state.currentSentence },
  };
}

/** Source and target language labels of the phrase card and the writing card. */
export function phraseLanguageLabels(
  state: SpeakContributeData | null | undefined,
  labels: { french: string; localLanguage: string },
): { source: string; target: string } {
  const local = state?.targetLanguage || labels.localLanguage;
  return state?.needsTranslationToFrench ? { source: local, target: labels.french } : { source: labels.french, target: local };
}
